import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import isoWeek from 'dayjs/plugin/isoWeek'
import weekday from 'dayjs/plugin/weekday'
import type { Query, QueryKey } from 'react-query'
import { DAILY, WEEKLY } from 'screens/Rostering/constants'
import {
  areSameWeek,
  getWeekBoundaries
} from 'screens/Rostering/utils.datetime'
import { resourceToQueryKeyMap } from './constants'
import type { InvalidateMessage } from './useQueryInvalidation'

dayjs.extend(weekday)
dayjs.extend(isoWeek)
dayjs.extend(isBetween)

export const getQueryInvalidationPredicate =
  ({
    changedResource,
    changedInterval,
    startDate,
    endDate,
    day
  }: InvalidateMessage) =>
  (query: Query<unknown, unknown, unknown, QueryKey>) => {
    const queryKeysToInvalidate = resourceToQueryKeyMap[changedResource]
    const dateToInvalidate = day ?? startDate
    const intervalTypeToInvalidate = changedInterval?.toLowerCase()
    const isQueryKeyArray = Array.isArray(query?.queryKey)

    const queryKey = isQueryKeyArray ? query?.queryKey?.[0] : query?.queryKey
    const date = isQueryKeyArray ? query?.queryKey?.[2] : undefined
    const intervalType = isQueryKeyArray
      ? (query?.queryKey?.[3] as string)?.toLowerCase()
      : undefined
    const dayjsDate = dayjs(date as string)

    const shouldSkipQueryKey =
      typeof queryKey !== 'string' || !queryKeysToInvalidate?.includes(queryKey)

    if (shouldSkipQueryKey) {
      return false
    }

    // If no date was provided invalidate everything
    if (!dateToInvalidate) {
      return queryKeysToInvalidate?.includes(queryKey)
    }

    if (!intervalType && startDate && endDate) {
      return dayjsDate.isBetween(startDate, endDate)
    }

    const areIntervalsInvalid =
      typeof intervalType !== 'string' ||
      typeof intervalTypeToInvalidate !== 'string' ||
      ![DAILY, WEEKLY].includes(intervalType) ||
      ![DAILY, WEEKLY].includes(intervalTypeToInvalidate)

    if (areIntervalsInvalid) {
      return false
    }

    if (intervalType === DAILY && intervalTypeToInvalidate === DAILY) {
      const isSameDay = dayjs(dateToInvalidate)
        .startOf('day')
        .isSame(dayjsDate.startOf('day'))

      if (isSameDay) {
        return true
      }
    }

    if (intervalType === DAILY && intervalTypeToInvalidate === WEEKLY) {
      const [monday, sunday] = getWeekBoundaries(dateToInvalidate)

      // if week to invalidate includes date
      if (dayjsDate.isBetween(monday, sunday)) {
        return true
      }
    }

    if (intervalType === WEEKLY && intervalTypeToInvalidate === DAILY) {
      const [monday, sunday] = getWeekBoundaries(dayjsDate)

      // if week to invalidate includes date
      if (dayjs(dateToInvalidate).isBetween(monday, sunday)) {
        return true
      }
    }

    if (intervalType === WEEKLY && intervalTypeToInvalidate === WEEKLY) {
      if (areSameWeek(dayjsDate, dateToInvalidate)) {
        return true
      }
    }

    return false
  }
